import { fmtArea, fmtEnergy, fmtPower } from "../../../utils/formatUnits";
import { EChartSvg } from "../../charts/EChartSvg";
import { comparisonOption } from "../charts/options";
import type {
  EnergyType,
  ReportData,
  ReportSubject,
} from "../../ReportComponent/model/types";
import type {
  ProjectionAreaStatsResponse,
  ResourceCharacterizationResponse,
} from "../../../services/api";
import { DataTable, Figure, Lead, SectionTitle, SubTitle, nf, pct } from "./primitives";

const ENERGY_TITLE: Record<EnergyType, string> = {
  solar: "Solar fotovoltaica",
  eolico: "Eólica",
};

/** Variável que resume o recurso em cada fonte. */
const RESOURCE_VARIABLE: Record<EnergyType, string> = {
  solar: "ghi",
  eolico: "velocidade",
};

interface Column {
  name: string;
  stats: ProjectionAreaStatsResponse | null;
  characterization: ResourceCharacterizationResponse | null;
  subject: ReportSubject | null;
}

interface Row {
  label: string;
  values: string[];
}

function variableMean(
  characterization: ResourceCharacterizationResponse | null,
  id: string
): number | null {
  const v = characterization?.variables.find((x) => x.id === id);
  return v?.available ? v.mean ?? null : null;
}

function specificEnergy(stats: ProjectionAreaStatsResponse | null): number | null {
  if (!stats || stats.propitious_area_km2 <= 0) return null;
  return stats.annual_energy_gwh / stats.propitious_area_km2;
}

function distance(subject: ReportSubject | null, id: string): string {
  if (!subject) return "—";
  const d = subject.infrastructure.find((x) => x.id === id)?.distanceKm;
  return d == null ? "—" : `${nf(d, 1)} km`;
}

function energyRows(energy: EnergyType, columns: Column[]): Row[] {
  const resourceId = RESOURCE_VARIABLE[energy];
  const resourceVar = columns
    .map((c) => c.characterization?.variables.find((v) => v.id === resourceId))
    .find(Boolean);
  return [
    {
      label: "Área propícia",
      values: columns.map((c) =>
        c.stats ? `${fmtArea(c.stats.propitious_area_km2)} (${pct(c.stats.percentage)})` : "—"
      ),
    },
    {
      label: "Potencial instalável",
      values: columns.map((c) => (c.stats ? fmtPower(c.stats.installable_gw) : "—")),
    },
    {
      label: "Geração anual estimada",
      values: columns.map((c) => (c.stats ? fmtEnergy(c.stats.annual_energy_gwh) : "—")),
    },
    {
      label: "Energia específica (GWh/km² propício·ano)",
      values: columns.map((c) => nf(specificEnergy(c.stats), 1)),
    },
    {
      label: `${resourceVar?.label ?? "Recurso médio"} (${resourceVar?.unit ?? ""})`,
      values: columns.map((c) => nf(variableMean(c.characterization, resourceId), 2)),
    },
  ];
}

export function ComparisonSection({ data, number }: { data: ReportData; number: string }) {
  const [a, b] = data.subjects;
  const reference = data.stateReference;

  const columnsFor = (energy: EnergyType): Column[] => [
    ...[a, b].map((s) => ({
      name: s.name,
      stats: s.analyses[energy]?.detailedLevel.stats ?? null,
      characterization: s.analyses[energy]?.characterization ?? null,
      subject: s,
    })),
    {
      name: "Pernambuco",
      stats: reference?.analyses[energy]?.stats ?? null,
      characterization: reference?.analyses[energy]?.characterization ?? null,
      subject: null,
    },
  ];

  const headers = [a.name, b.name, "Pernambuco (referência)"];
  const tableColumns = [
    { header: "Indicador", render: (r: Row) => r.label, width: "34%" },
    ...headers.map((h, i) => ({
      header: h,
      align: "right" as const,
      render: (r: Row) => r.values[i],
    })),
  ];

  const generalRows: Row[] = [
    {
      label: "Área analisada",
      values: [
        a.areaKm2 != null ? fmtArea(a.areaKm2) : "—",
        b.areaKm2 != null ? fmtArea(b.areaKm2) : "—",
        "—",
      ],
    },
    {
      label: "Município (predominante)",
      values: [a.municipalityName ?? "—", b.municipalityName ?? "—", "—"],
    },
    {
      label: "Declividade média (°)",
      values: [
        ...[a, b].map((s) =>
          nf(variableMean(s.analyses[data.energies[0]]?.characterization ?? null, "declividade"), 1)
        ),
        nf(
          variableMean(reference?.analyses[data.energies[0]]?.characterization ?? null, "declividade"),
          1
        ),
      ],
    },
    { label: "Subestação mais próxima", values: [distance(a, "subestacao"), distance(b, "subestacao"), "—"] },
    {
      label: "Linha de transmissão mais próxima",
      values: [distance(a, "linha_transmissao"), distance(b, "linha_transmissao"), "—"],
    },
    { label: "Rodovia federal mais próxima", values: [distance(a, "rodovia_federal"), distance(b, "rodovia_federal"), "—"] },
    { label: "Porto mais próximo", values: [distance(a, "porto"), distance(b, "porto"), "—"] },
    { label: "Adutora mais próxima", values: [distance(a, "adutora"), distance(b, "adutora"), "—"] },
  ];

  return (
    <section>
      <SectionTitle number={number}>Comparativo entre as áreas</SectionTitle>
      <Lead>
        Indicadores-chave das duas Marcações no nível {data.levelLabel}, com Pernambuco
        continental como referência. Os valores absolutos do estado dão a ordem de
        grandeza; a comparação justa entre áreas de tamanhos diferentes é pelos
        indicadores relativos — percentual propício, energia específica e recurso
        médio —, que são os dos gráficos.
      </Lead>

      <SubTitle>Localização e infraestrutura</SubTitle>
      <DataTable columns={tableColumns} rows={generalRows} rowKey={(r) => r.label} />

      {data.energies.map((energy) => {
        const columns = columnsFor(energy);
        const rows = energyRows(energy, columns);
        const resourceRow = rows[rows.length - 1];
        return (
          <div key={energy}>
            <SubTitle>{ENERGY_TITLE[energy]}</SubTitle>
            <DataTable columns={tableColumns} rows={rows} rowKey={(r) => r.label} />
            <div className="grid grid-cols-3 gap-3">
              <Figure caption="Percentual da área propícia ao nível escolhido.">
                <EChartSvg
                  ariaLabel={`Área propícia (%) — ${ENERGY_TITLE[energy]}`}
                  width={220}
                  height={120}
                  option={comparisonOption(
                    "Área propícia",
                    "%",
                    columns.map((c) => ({ name: c.name, value: c.stats?.percentage ?? null })),
                    (v) => pct(v)
                  )}
                />
              </Figure>
              <Figure caption="Geração anual por km² de área propícia.">
                <EChartSvg
                  ariaLabel={`Energia específica — ${ENERGY_TITLE[energy]}`}
                  width={220}
                  height={120}
                  option={comparisonOption(
                    "Energia específica",
                    "GWh/km²",
                    columns.map((c) => ({ name: c.name, value: specificEnergy(c.stats) })),
                    (v) => nf(v, 1)
                  )}
                />
              </Figure>
              <Figure caption="Média do recurso na área (dados aeroespaciais).">
                <EChartSvg
                  ariaLabel={`${resourceRow.label} — ${ENERGY_TITLE[energy]}`}
                  width={220}
                  height={120}
                  option={comparisonOption(
                    energy === "solar" ? "GHI médio" : "Vento médio a 100 m",
                    energy === "solar" ? "kWh/m²/dia" : "m/s",
                    columns.map((c) => ({
                      name: c.name,
                      value: variableMean(c.characterization, RESOURCE_VARIABLE[energy]),
                    })),
                    (v) => nf(v, 2)
                  )}
                />
              </Figure>
            </div>
          </div>
        );
      })}
    </section>
  );
}
